import React from "react";
import { BrandLogo } from "@/components/Brand";
import { Link } from "react-router-dom";
import { Compass, ArrowLeft, Mail, Phone, MessageSquare } from "lucide-react";
import SeoHead from "@/components/SeoHead";

/**
 * /support — public support page required as a live URL by Apple App
 * Store and Google Play Store. Keep it lightweight and human.
 */
export default function Support() {
    return (
        <div className="min-h-screen bg-cream">
            <SeoHead
                title="Memory Lane Care — Support & Contact"
                description="Get in touch with the Memory Lane Care team. Help with your account, partner inquiries, technical support, and feedback."
                canonicalPath="/support"
            />
            <header className="bg-cream/80 backdrop-blur-sm sticky top-0 z-30 border-b border-[#E2E8F0]/60">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <BrandLogo className="h-6" />
                    </Link>
                    <Link to="/" className="text-sm text-teal-dark hover:underline inline-flex items-center gap-1.5">
                        <ArrowLeft size={14} strokeWidth={1.8} /> Back home
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16" data-testid="support-page">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">Support</p>
                <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#12776F]">
                    We&apos;re here. How can we help?
                </h1>
                <p className="mt-4 text-lg text-[#4C5551] leading-relaxed">
                    Memory Lane Care is a small team — when you reach out, a real human reads
                    your email. We aim to respond within 1 business day.
                </p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <article className="ml-card p-6 bg-white">
                        <div className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center mb-3">
                            <Mail size={18} strokeWidth={1.8} />
                        </div>
                        <h2 className="font-heading text-xl text-[#1F2624]">Email us</h2>
                        <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                            For account help, billing questions, or anything technical.
                        </p>
                        <a href="mailto:ashlee@asvmedicalservices.com" className="ml-btn-primary !min-h-[44px] mt-4 inline-flex items-center gap-2">
                            ashlee@asvmedicalservices.com
                        </a>
                    </article>

                    <article className="ml-card p-6 bg-white">
                        <div className="w-10 h-10 rounded-2xl bg-coral-light text-coral-dark flex items-center justify-center mb-3">
                            <MessageSquare size={18} strokeWidth={1.8} />
                        </div>
                        <h2 className="font-heading text-xl text-[#1F2624]">Talk to Ashlee</h2>
                        <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                            Want to talk through your situation? Book a free 15-minute consult.
                        </p>
                        <Link to="/navigator" className="ml-btn-coral !min-h-[44px] mt-4 inline-flex items-center gap-2">
                            Book a session
                        </Link>
                    </article>
                </div>

                <section className="ml-card p-6 mt-6 bg-stage_early/20 border border-stage_early/30">
                    <div className="flex items-start gap-3">
                        <Phone size={20} strokeWidth={1.8} className="text-stage_early-dark shrink-0 mt-1" />
                        <div>
                            <h2 className="font-heading text-lg text-[#1F2624]">In a crisis?</h2>
                            <p className="mt-1 text-sm text-[#1F2624] leading-relaxed">
                                Call the <strong>Alzheimer&apos;s Association 24/7 Helpline</strong> at{" "}
                                <a href="tel:18002723900" className="text-teal-dark font-semibold">1-800-272-3900</a>.
                                If your loved one or you are in immediate danger, dial{" "}
                                <a href="tel:911" className="text-coral-dark font-semibold">911</a> or, in the US,{" "}
                                <a href="tel:988" className="text-coral-dark font-semibold">988</a> for mental-health crisis.
                            </p>
                        </div>
                    </div>
                </section>

                <footer className="mt-12 text-xs text-[#6E7A74]">
                    Memory Lane Care · <Link to="/privacy" className="hover:underline">Privacy</Link> · <Link to="/" className="hover:underline">Home</Link>
                </footer>
            </main>
        </div>
    );
}
